import { motion } from 'motion/react';
import styles from './PricingCard.module.scss';
import type { SubscriptionTier } from '@/types/subscription';

interface PricingCardProps {
  tier: SubscriptionTier;
  title: string;
  price: number;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
  onSelect: () => void;
  buttonText: string;
}

export function PricingCard({
  title,
  price,
  features,
  isPopular = false,
  isCurrent = false,
  onSelect,
  buttonText,
}: PricingCardProps) {
  return (
    <motion.div
      className={`${styles.pricingCard} ${isPopular ? styles['pricingCard--popular'] : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      {/* Animated gradient border */}
      <div className={styles.pricingCard__gradientBorder} />

      {isPopular && (
        <div className={styles.pricingCard__badge}>Most Popular</div>
      )}

      <div className={styles.pricingCard__header}>
        <h3 className={styles.pricingCard__title}>{title}</h3>
        <div className={styles.pricingCard__price}>
          {price === 0 ? (
            <span className={styles.pricingCard__priceAmount}>Free</span>
          ) : (
            <>
              <span className={styles.pricingCard__priceSymbol}>$</span>
              <span className={styles.pricingCard__priceAmount}>{price.toFixed(2)}</span>
              <span className={styles.pricingCard__pricePeriod}>/month</span>
            </>
          )}
        </div>
      </div>

      <ul className={styles.pricingCard__features}>
        {features.map((feature, index) => (
          <motion.li
            key={index}
            className={styles.pricingCard__feature}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <svg
              className={styles.pricingCard__checkmark}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {feature}
          </motion.li>
        ))}
      </ul>

      <button
        className={`${styles.pricingCard__button} ${isPopular ? styles['pricingCard__button--popular'] : ''
          }`}
        onClick={onSelect}
        disabled={isCurrent}
      >
        {buttonText}
      </button>
    </motion.div>
  );
}
